import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import LoginAndSignupPopup from "./LoginAndSignup";

import GrowniverseLogo from "../Photos/Logo/growniverse-logo-full-bordered.svg";


const Header: React.FC = () => {

  /* ============================================================
     STATE
  ============================================================ */

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] =
    useState(false);


  /* ============================================================
     ROUTER
  ============================================================ */

  const location = useLocation();

  const isHomePage =
    location.pathname === "/";


  /* ============================================================
     QUERY PARAMETERS
  ============================================================ */

  const query = useMemo(
    () =>
      new URLSearchParams(
        location.search
      ),
    [location.search]
  );

  const runOnce = useRef(false);


  /* ============================================================
     NAVIGATION
  ============================================================ */

  const navigationItems = [
    {
      label: "Home",
      path: "/",
    },
    {
      label: "About Us",
      path: "/about",
    },
    {
      label: "Contact Us",
      path: "/contact",
    },
    {
      label: "Courses",
      path: "/courses",
    },
    {
      label: "Certificate",
      path: "/certificate-verification",
    },
  ];


  /* ============================================================
     ACTIVE PAGE
  ============================================================ */

  const isActive = (path: string) => {

    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };


  /* ============================================================
     LOGIN
  ============================================================ */

  const openLogin = () => {

    setMobileMenuOpen(false);

    setIsModalOpen(true);
  };


  const closeLogin = () => {

    setIsModalOpen(false);
  };


  /* ============================================================
     AFFILIATE LOGIN
  ============================================================ */

  useEffect(() => {

    if (runOnce.current) {
      return;
    }

    const sponsorGID =
      query.get("affiliatelink");

    if (sponsorGID) {
      setIsModalOpen(true);
    }

    runOnce.current = true;

  }, [query]);


  /* ============================================================
     CLOSE MENU WHEN PAGE CHANGES
  ============================================================ */

  useEffect(() => {

    setMobileMenuOpen(false);

  }, [location.pathname]);


  /* ============================================================
     PREVENT BODY SCROLL WHEN MOBILE MENU OPEN
  ============================================================ */

  useEffect(() => {

    if (mobileMenuOpen) {

      document.body.style.overflow = "hidden";

    } else {

      document.body.style.overflow = "";

    }

    return () => {

      document.body.style.overflow = "";

    };

  }, [mobileMenuOpen]);


  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>

      {/* ========================================================
          HEADER
      ======================================================== */}

      <header
        className={
          isHomePage
            ? "growniverse-header growniverse-header-home"
            : "growniverse-header"
        }
      >

        {/* ======================================================
            BACKGROUND EFFECTS
        ====================================================== */}

        <div className="growniverse-header-grid" />

        <div
          className="
            growniverse-header-glow
            growniverse-header-glow-left
          "
        />

        <div
          className="
            growniverse-header-glow
            growniverse-header-glow-right
          "
        />


        {/* ======================================================
            NAVBAR
        ====================================================== */}

        <nav
          className="growniverse-navbar"
          aria-label="Main navigation"
        >

          <div className="growniverse-navbar-inner">


            {/* ==================================================
                LOGO
            ================================================== */}

            <Link
              to="/"
              className="growniverse-logo-link"
              aria-label="Growniverse Home"
            >

              <img
                src={GrowniverseLogo}
                alt="Growniverse"
                className="growniverse-logo"
              />

            </Link>


            {/* ==================================================
                DESKTOP NAVIGATION
            ================================================== */}

            <div className="growniverse-desktop-navigation">

              {navigationItems.map((item) => {

                const active =
                  isActive(item.path);

                return (

                  <Link
                    key={item.path}
                    to={item.path}
                    className={
                      active
                        ? "growniverse-nav-link growniverse-nav-link-active"
                        : "growniverse-nav-link"
                    }
                  >

                    {item.label}

                    {active && (

                      <span
                        className="
                          growniverse-nav-active-line
                        "
                      />

                    )}

                  </Link>

                );

              })}

            </div>


            {/* ==================================================
                RIGHT ACTIONS
            ================================================== */}

            <div className="growniverse-header-actions">

              {/* LOGIN */}

              <button
                type="button"
                className="growniverse-login-button"
                onClick={openLogin}
              >
                Login
              </button>


              {/* MOBILE MENU */}

              <button
                type="button"
                className={
                  mobileMenuOpen
                    ? "growniverse-menu-button growniverse-menu-button-active"
                    : "growniverse-menu-button"
                }
                onClick={() =>
                  setMobileMenuOpen(
                    previous => !previous
                  )
                }
                aria-label={
                  mobileMenuOpen
                    ? "Close menu"
                    : "Open menu"
                }
                aria-expanded={
                  mobileMenuOpen
                }
              >

                <span />
                <span />
                <span />

              </button>

            </div>

          </div>

        </nav>


        {/* ======================================================
            MOBILE MENU
        ====================================================== */}

        {mobileMenuOpen && (

          <div
            className="growniverse-mobile-menu"
          >

            <div
              className="
                growniverse-mobile-menu-content
              "
            >

              {navigationItems.map(
                item => {

                  const active =
                    isActive(item.path);

                  return (

                    <Link
                      key={item.path}
                      to={item.path}
                      className={
                        active
                          ? "growniverse-mobile-link growniverse-mobile-link-active"
                          : "growniverse-mobile-link"
                      }
                      onClick={() =>
                        setMobileMenuOpen(false)
                      }
                    >

                      <span>
                        {item.label}
                      </span>

                      {active && (

                        <span
                          className="
                            growniverse-mobile-active-dot
                          "
                        />

                      )}

                    </Link>

                  );

                }
              )}


              {/* MOBILE LOGIN */}

              <button
                type="button"
                className="
                  growniverse-mobile-login
                "
                onClick={openLogin}
              >
                Login
              </button>

            </div>

          </div>

        )}

        {/* ======================================================
            SCAN LINE
        ====================================================== */}

        <div
          className="
            growniverse-header-scan-line
          "
        />

      </header>


      {/* ========================================================
          LOGIN POPUP
      ======================================================== */}

      {isModalOpen && (

        <LoginAndSignupPopup
          onClose={closeLogin}
        />

      )}


      {/* ========================================================
          CSS
      ======================================================== */}

      <style>
        {`

/* ============================================================
   GLOBAL RESET FOR THIS HEADER
   THIS IS THE IMPORTANT FIX
============================================================ */

.growniverse-header,
.growniverse-header *,
.growniverse-header *::before,
.growniverse-header *::after {
  box-sizing: border-box;
}


/*
   IMPORTANT:

   Reset BOTH the normal header and the home header.

   If somewhere else in your project you have something like:

   .growniverse-header-home {
      padding-top: 30px;
   }

   this will remove it.
*/

.growniverse-header,
.growniverse-header-home {
  position: relative !important;

  display: block !important;

  width: 100vw !important;
  max-width: 100vw !important;

  height: auto !important;
  min-height: 0 !important;

  margin-top: 0 !important;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  margin-left: calc(50% - 50vw) !important;

  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;

  top: 0 !important;

  background: #02070d;

  border: 0;

  z-index: 1000;

  overflow: visible;
}


/* ============================================================
   NAVBAR
============================================================ */

.growniverse-navbar {
  position: relative !important;

  display: block !important;

  width: 100% !important;
  max-width: 100% !important;

  height: 88px !important;
  min-height: 88px !important;

  margin: 0 !important;

  padding: 0 !important;

  top: 0 !important;

  background:
    linear-gradient(
      90deg,
      #020810 0%,
      #030b13 50%,
      #020810 100%
    );

  border-top: 0 !important;

  border-left: 0 !important;

  border-right: 0 !important;

  border-bottom:
    1px solid
    rgba(0, 210, 255, 0.10);

  z-index: 1001;
}


/* ============================================================
   NAVBAR INNER
============================================================ */

.growniverse-navbar-inner {
  position: relative;

  display: flex;

  align-items: center;

  justify-content: space-between;

  width: 100% !important;
  max-width: 100% !important;

  height: 88px !important;
  min-height: 88px !important;

  margin: 0 !important;

  padding:
    0 42px !important;
}


/* ============================================================
   LOGO
============================================================ */

.growniverse-logo-link {
  display: flex;

  align-items: center;

  justify-content: flex-start;

  width: auto;

  height: 100%;

  margin: 0 !important;

  padding: 0 !important;

  flex-shrink: 0;

  text-decoration: none;
}


.growniverse-logo {
  display: block;

  width: 145px;

  height: auto;

  max-width: 145px;

  max-height: 64px;

  margin: 0 !important;

  padding: 0 !important;

  object-fit: contain;

  transition:
    transform 0.25s ease,
    filter 0.25s ease;
}


.growniverse-logo-link:hover .growniverse-logo {
  transform: scale(1.03);

  filter:
    drop-shadow(
      0 0 12px
      rgba(0, 220, 255, 0.22)
    );
}


/* ============================================================
   BACKGROUND GRID
============================================================ */

.growniverse-header-grid {
  position: absolute;

  inset: 0;

  width: 100%;
  height: 100%;

  pointer-events: none;

  opacity: 0.28;

  background-image:
    linear-gradient(
      rgba(0, 200, 255, 0.035) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(0, 200, 255, 0.035) 1px,
      transparent 1px
    );

  background-size: 42px 42px;
}


/* ============================================================
   GLOW
============================================================ */

.growniverse-header-glow {
  position: absolute;

  width: 300px;
  height: 120px;

  border-radius: 50%;

  pointer-events: none;

  filter: blur(40px);

  opacity: 0.18;
}


.growniverse-header-glow-left {
  left: -120px;
  top: -80px;

  background: #00d9ff;
}


.growniverse-header-glow-right {
  right: -120px;
  top: -80px;

  background: #526cff;
}


/* ============================================================
   DESKTOP NAVIGATION
============================================================ */

.growniverse-desktop-navigation {
  display: flex;

  align-items: center;

  justify-content: center;

  gap: 8px;

  flex: 1;

  margin:
    0 35px;
}


.growniverse-nav-link {
  position: relative;

  display: flex;

  align-items: center;

  justify-content: center;

  min-height: 42px;

  padding:
    0 17px;

  color: #a9b7c8;

  text-decoration: none;

  font-size: 14px;

  font-weight: 600;

  white-space: nowrap;

  border-radius: 10px;

  transition:
    color 0.2s ease,
    background 0.2s ease;
}


.growniverse-nav-link:hover {
  color: #ffffff;

  background:
    rgba(0, 210, 255, 0.06);
}


.growniverse-nav-link-active {
  color: #ffffff;
}


.growniverse-nav-active-line {
  position: absolute;

  left: 15px;
  right: 15px;

  bottom: 1px;

  height: 2px;

  border-radius: 10px;

  background:
    linear-gradient(
      90deg,
      #00d9ff,
      #6ee7ff
    );

  box-shadow:
    0 0 10px
    rgba(0, 217, 255, 0.65);
}


/* ============================================================
   HEADER ACTIONS
============================================================ */

.growniverse-header-actions {
  display: flex;

  align-items: center;

  justify-content: flex-end;

  gap: 10px;

  flex-shrink: 0;

  margin:
    0 0 0 auto !important;

  padding: 0 !important;
}


/* ============================================================
   LOGIN BUTTON
============================================================ */

.growniverse-login-button {
  display: flex;

  align-items: center;

  justify-content: center;

  height: 44px;

  min-width: 88px;

  padding:
    0 22px;

  margin: 0;

  border-radius: 11px;

  border:
    1px solid
    rgba(0, 213, 255, 0.65);

  background:
    linear-gradient(
      180deg,
      rgba(0, 214, 255, 0.08),
      rgba(0, 214, 255, 0.025)
    );

  color: #ffffff;

  font-size: 14px;

  font-weight: 700;

  cursor: pointer;

  transition:
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}


.growniverse-login-button:hover {
  background:
    rgba(0, 214, 255, 0.12);

  box-shadow:
    0 0 20px
    rgba(0, 214, 255, 0.18);

  transform:
    translateY(-1px);
}


/* ============================================================
   HAMBURGER
============================================================ */

.growniverse-menu-button {
  display: none;

  width: 48px;
  height: 48px;

  padding: 0;
  margin: 0;

  border-radius: 12px;

  border:
    1px solid
    rgba(0, 213, 255, 0.65);

  background:
    rgba(0, 213, 255, 0.045);

  cursor: pointer;

  align-items: center;
  justify-content: center;

  flex-direction: column;

  gap: 6px;
}


.growniverse-menu-button span {
  display: block;

  width: 26px;
  height: 2px;

  border-radius: 5px;

  background: #dffaff;

  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}


/* ============================================================
   HAMBURGER → X
============================================================ */

.growniverse-menu-button-active span:nth-child(1) {
  transform:
    translateY(8px)
    rotate(45deg);
}


.growniverse-menu-button-active span:nth-child(2) {
  opacity: 0;
}


.growniverse-menu-button-active span:nth-child(3) {
  transform:
    translateY(-8px)
    rotate(-45deg);
}


/* ============================================================
   MOBILE MENU
============================================================ */

.growniverse-mobile-menu {
  position: absolute;

  top: 100%;

  left: 0;

  width: 100%;

  margin: 0 !important;

  padding: 0 !important;

  background:
    linear-gradient(
      180deg,
      #02070d 0%,
      #030a11 100%
    );

  border-top: 0 !important;

  border-bottom:
    1px solid
    rgba(0, 213, 255, 0.20);

  box-shadow:
    0 20px 40px
    rgba(0, 0, 0, 0.55);

  z-index: 99999;

  overflow: hidden;
}


.growniverse-mobile-menu-content {
  width: 100%;

  padding:
    14px 20px 22px;

  display: flex;

  flex-direction: column;

  gap: 5px;
}


/* ============================================================
   MOBILE LINKS
============================================================ */

.growniverse-mobile-link {
  min-height: 52px;

  padding:
    0 20px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  color: #aab7c7;

  text-decoration: none;

  font-size: 16px;

  font-weight: 600;

  border-radius: 11px;

  border:
    1px solid transparent;

  transition:
    background 0.2s ease,
    color 0.2s ease;
}


.growniverse-mobile-link:hover {
  color: #ffffff;

  background:
    rgba(0, 213, 255, 0.06);
}


.growniverse-mobile-link-active {
  color: #ffffff;

  background:
    rgba(0, 213, 255, 0.08);

  border-color:
    rgba(0, 213, 255, 0.18);
}


.growniverse-mobile-active-dot {
  width: 8px;
  height: 8px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #00d9ff;

  box-shadow:
    0 0 12px
    rgba(0, 217, 255, 0.9);
}


/* ============================================================
   MOBILE LOGIN
============================================================ */

.growniverse-mobile-login {
  width: 100%;

  height: 56px;

  margin-top: 14px;

  border-radius: 11px;

  border:
    1px solid
    rgba(0, 213, 255, 0.55);

  background:
    rgba(0, 213, 255, 0.07);

  color: #ffffff;

  font-size: 16px;

  font-weight: 700;

  cursor: pointer;
}


/* ============================================================
   SCAN LINE
============================================================ */

.growniverse-header-scan-line {
  position: absolute;

  bottom: -1px;

  left: 0;
  right: 0;

  height: 1px;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(0, 220, 255, 0.55),
      transparent
    );

  pointer-events: none;

  z-index: 1002;
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 1000px) {

  .growniverse-navbar-inner {
    padding-left: 25px !important;
    padding-right: 25px !important;
  }


  .growniverse-desktop-navigation {
    gap: 2px;

    margin:
      0 15px;
  }


  .growniverse-nav-link {
    padding:
      0 10px;

    font-size: 13px;
  }


  .growniverse-logo {
    width: 125px;

    max-width: 125px;
  }
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 768px) {

  /*
     ==========================================================
     MAIN MOBILE HEADER

     IMPORTANT FIX:
     No padding
     No margin-top
     No extra height
     No black block above navbar
     ==========================================================
  */

  .growniverse-header,
  .growniverse-header-home {
    position: relative !important;

    display: block !important;

    width: 100vw !important;
    max-width: 100vw !important;

    height: auto !important;
    min-height: 0 !important;

    margin-top: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0 !important;
    margin-left:
      calc(50% - 50vw) !important;

    padding: 0 !important;

    top: 0 !important;

    background: #000000 !important;

    border: 0 !important;

    overflow: visible !important;
  }


  /*
     ==========================================================
     MOBILE NAVBAR

     This MUST be the first visible element.
     ==========================================================
  */

  .growniverse-navbar {
    position: relative !important;

    display: block !important;

    width: 100% !important;

    height: 86px !important;
    min-height: 86px !important;

    margin: 0 !important;

    padding: 0 !important;

    top: 0 !important;

    background: #000000 !important;

    border-top: 0 !important;

    border-left: 0 !important;

    border-right: 0 !important;

    border-bottom:
      1px solid
      rgba(0, 213, 255, 0.20);

    z-index: 1001;
  }


  /*
     ==========================================================
     MOBILE INNER
     ==========================================================
  */

  .growniverse-navbar-inner {
    position: relative !important;

    display: flex !important;

    align-items: center !important;

    justify-content: space-between !important;

    width: 100% !important;

    height: 86px !important;
    min-height: 86px !important;

    margin: 0 !important;

    padding:
      0 14px !important;

    background: #000000 !important;
  }


  /*
     ==========================================================
     LOGO
     ==========================================================
  */

  .growniverse-logo-link {
    display: flex !important;

    align-items: center !important;

    justify-content: flex-start !important;

    width: auto !important;

    height: 86px !important;

    margin:
      0 auto 0 0 !important;

    padding: 0 !important;

    flex-shrink: 0 !important;
  }


  .growniverse-logo {
    display: block !important;

    width: 120px !important;

    max-width: 120px !important;

    height: auto !important;

    max-height: 62px !important;

    margin: 0 !important;

    padding: 0 !important;

    object-fit: contain !important;
  }


  /*
     ==========================================================
     HIDE DESKTOP NAV
     ==========================================================
  */

  .growniverse-desktop-navigation {
    display: none !important;
  }


  /*
     ==========================================================
     RIGHT ACTIONS
     ==========================================================
  */

  .growniverse-header-actions {
    display: flex !important;

    align-items: center !important;

    justify-content: flex-end !important;

    gap: 8px !important;

    width: auto !important;

    margin:
      0 0 0 auto !important;

    padding: 0 !important;

    flex-shrink: 0 !important;
  }


  /*
     ==========================================================
     LOGIN
     ==========================================================
  */

  .growniverse-login-button {
    display: flex !important;

    align-items: center !important;

    justify-content: center !important;

    height: 48px !important;

    min-width: 78px !important;

    padding:
      0 17px !important;

    margin: 0 !important;

    font-size: 14px !important;
  }


  /*
     ==========================================================
     HAMBURGER
     ==========================================================
  */

  .growniverse-menu-button {
    display: flex !important;

    align-items: center !important;

    justify-content: center !important;

    width: 48px !important;

    height: 48px !important;

    margin: 0 !important;

    padding: 0 !important;

    flex-shrink: 0 !important;
  }


  /*
     ==========================================================
     MOBILE MENU

     It starts immediately BELOW the navbar.
     ==========================================================
  */

  .growniverse-mobile-menu {
    position: absolute !important;

    top: 86px !important;

    left: 0 !important;

    right: auto !important;

    width: 100vw !important;

    max-width: 100vw !important;

    margin: 0 !important;

    padding: 0 !important;

    background:
      linear-gradient(
        180deg,
        #02070d 0%,
        #02080e 100%
      );

    border-top: 0 !important;

    border-bottom:
      1px solid
      rgba(0, 213, 255, 0.20);

    box-shadow:
      0 18px 40px
      rgba(0, 0, 0, 0.60);

    z-index: 99999 !important;

    overflow: hidden;
  }


  .growniverse-mobile-menu-content {
    width: 100% !important;

    padding:
      14px
      16px
      24px !important;

    display: flex !important;

    flex-direction: column !important;

    gap: 5px !important;
  }


  /*
     ==========================================================
     REMOVE HEADER EFFECTS ON MOBILE
     ==========================================================
  */

  .growniverse-header-glow,
  .growniverse-header-grid {
    display: none !important;
  }
}


/* ============================================================
   SMALL MOBILE
============================================================ */

@media (max-width: 420px) {

  .growniverse-navbar {
    height: 80px !important;

    min-height: 80px !important;
  }


  .growniverse-navbar-inner {
    height: 80px !important;

    min-height: 80px !important;

    padding-left:
      12px !important;

    padding-right:
      12px !important;
  }


  .growniverse-logo-link {
    height: 80px !important;
  }


  .growniverse-logo {
    width: 112px !important;

    max-width: 112px !important;

    max-height: 58px !important;
  }


  .growniverse-login-button {
    height: 46px !important;

    min-width: 70px !important;

    padding:
      0 14px !important;
  }


  .growniverse-menu-button {
    width: 46px !important;

    height: 46px !important;
  }


  /*
     Menu matches 80px navbar
  */

  .growniverse-mobile-menu {
    top: 80px !important;
  }
}


/* ============================================================
   VERY SMALL MOBILE
============================================================ */

@media (max-width: 340px) {

  .growniverse-navbar-inner {
    padding-left:
      10px !important;

    padding-right:
      10px !important;
  }


  .growniverse-logo {
    width: 98px !important;

    max-width: 98px !important;
  }


  .growniverse-header-actions {
    gap: 6px !important;
  }


  .growniverse-login-button {
    min-width: 64px !important;

    padding:
      0 11px !important;
  }


  .growniverse-menu-button {
    width: 43px !important;

    height: 43px !important;
  }
}

        `}
      </style>

    </>
  );
};


export default Header;